import { Select } from '@mantine/core'
import { useDebounce } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { useMedplum } from '@medplum/react'
import type { Coding, Condition, ValueSetExpansionContains } from '@medplum/fhirtypes'
import { useEffect, useMemo, useState } from 'react'
import { logError } from '@/utils/errorUtils'
import { ICD10CM_SYSTEM } from '@/model/middleware-types'

const ICD10CM_DX_VALUE_SET_URL = 'https://behuman.health/fhir/ValueSet/icd10cm-2026'

interface DxCodingSelectProps {
  value?: Coding
  patientId?: string
  valueSetUrl?: string
  placeholder?: string
  /**
   * Billing pickers only (PD-2014): the date of service whose ICD-10-CM code set applies.
   * A category code shows disabled, followed by the billable codes under it.
   */
  billableOn?: string
  onChange: (value?: Coding) => void
}

type DxOption = {
  value: string
  label: string
  coding: Coding
  disabled?: boolean
}

type Billability = { billable: boolean; children: { code: string; display: string }[] }

type ExpansionNode = ValueSetExpansionContains & { contains?: ValueSetExpansionContains[] }

const toCodingKey = (coding: Coding): string => `${coding.system || ''}|${coding.code || ''}`

const toOption = (coding: Coding): DxOption => {
  const code = coding.code || ''
  const display = coding.display || code || 'Unknown diagnosis'

  return {
    value: toCodingKey(coding),
    label: code ? `${code} - ${display}` : display,
    coding,
  }
}

const isIcd10Coding = (coding: Coding | undefined): coding is Coding => {
  if (!coding?.code || !coding.system) {
    return false
  }

  const system = coding.system.toLowerCase()
  return system.includes('icd-10') || system.includes('icd10')
}

const uniqueCodings = (codings: Coding[]): Coding[] => {
  const seen = new Set<string>()
  const result: Coding[] = []

  for (const coding of codings) {
    const key = toCodingKey(coding)
    if (!coding.code || seen.has(key)) {
      continue
    }

    seen.add(key)
    result.push(coding)
  }

  return result
}

const flattenContains = (nodes: ValueSetExpansionContains[] = []): ValueSetExpansionContains[] => {
  const flat: ValueSetExpansionContains[] = []
  const queue = [...nodes] as ExpansionNode[]

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) {
      continue
    }

    flat.push(current)

    if (Array.isArray(current.contains) && current.contains.length > 0) {
      queue.push(...(current.contains as ExpansionNode[]))
    }
  }

  return flat
}

export function DxCodingSelect({
  value,
  patientId,
  valueSetUrl,
  placeholder,
  billableOn,
  onChange,
}: DxCodingSelectProps) {
  const medplum = useMedplum()
  const client = useClient()
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [recentCodings, setRecentCodings] = useState<Coding[]>([])
  const [searchCodings, setSearchCodings] = useState<Coding[]>([])
  const [hasLoadedRecent, setHasLoadedRecent] = useState(false)
  const [hasRecentDiagnoses, setHasRecentDiagnoses] = useState(false)

  const debouncedSearch = useDebounce(search, 250)

  useEffect(() => {
    let active = true

    const loadRecentDiagnoses = async () => {
      if (!patientId) {
        if (active) {
          setRecentCodings([])
          setHasRecentDiagnoses(false)
          setHasLoadedRecent(true)
        }
        return
      }

      try {
        const conditions = await medplum.searchResources('Condition', {
          subject: `Patient/${patientId}`,
          _sort: '-recorded-date,-asserted-date',
          _count: '200',
        })

        if (!active) {
          return
        }

        const codings: Coding[] = []
        for (const condition of conditions as Condition[]) {
          const coding = condition.code?.coding?.find(isIcd10Coding)
          if (coding) {
            codings.push(coding)
          }
        }

        const uniqueRecent = uniqueCodings(codings).slice(0, 15)
        setRecentCodings(uniqueRecent)
        setHasRecentDiagnoses(uniqueRecent.length > 0)
        setHasLoadedRecent(true)
      } catch (error) {
        logError('call', 'Failed to load recent diagnoses', error)
        if (active) {
          setRecentCodings([])
          setHasRecentDiagnoses(false)
          setHasLoadedRecent(true)
        }
      }
    }

    loadRecentDiagnoses()

    return () => {
      active = false
    }
  }, [medplum, patientId])

  useEffect(() => {
    let active = true

    const loadFromValueSet = async () => {
      if (patientId && !hasLoadedRecent) {
        return
      }

      const filter = debouncedSearch.trim()
      const shouldUseRecent =
        filter.length === 0 && patientId && hasLoadedRecent && hasRecentDiagnoses

      if (shouldUseRecent) {
        setSearchCodings([])
        return
      }

      try {
        setLoading(true)
        const expanded = await medplum.valueSetExpand({
          url: valueSetUrl || ICD10CM_DX_VALUE_SET_URL,
          filter,
          count: filter ? 20 : 15,
          offset: 0,
        })

        if (!active) {
          return
        }

        let contains = flattenContains(expanded.expansion?.contains || [])
        let codings = contains
          .filter((item): item is ValueSetExpansionContains => Boolean(item.code))
          .map((item) => ({
            system: item.system || ICD10CM_SYSTEM,
            code: item.code,
            display: item.display || item.code,
          }))

        // Some servers return empty expansion for empty filter; retry with a broad prefix.
        if (!filter && codings.length === 0) {
          const fallbackExpanded = await medplum.valueSetExpand({
            url: valueSetUrl || ICD10CM_DX_VALUE_SET_URL,
            filter: 'a',
            count: 15,
            offset: 0,
          })
          contains = flattenContains(fallbackExpanded.expansion?.contains || [])
          codings = contains
            .filter((item): item is ValueSetExpansionContains => Boolean(item.code))
            .map((item) => ({
              system: item.system || ICD10CM_SYSTEM,
              code: item.code,
              display: item.display || item.code,
            }))
        }

        setSearchCodings(uniqueCodings(codings))
      } catch (error) {
        logError('call', 'Failed to load diagnoses from ValueSet', error)
        if (active) {
          setSearchCodings([])
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadFromValueSet()

    return () => {
      active = false
    }
  }, [debouncedSearch, hasLoadedRecent, hasRecentDiagnoses, medplum, patientId, valueSetUrl])

  const codings = useMemo(() => {
    const source =
      debouncedSearch.trim().length === 0 && patientId && hasLoadedRecent && hasRecentDiagnoses
        ? recentCodings
        : searchCodings

    return value?.code ? uniqueCodings([value, ...source]) : source
  }, [
    debouncedSearch,
    hasLoadedRecent,
    hasRecentDiagnoses,
    patientId,
    recentCodings,
    searchCodings,
    value,
  ])

  // Keyed on the joined codes, not the array, so a re-render with the same options does not refetch.
  const codesKey = codings
    .map((coding) => coding.code ?? '')
    .filter(Boolean)
    .join(',')
  const [billability, setBillability] = useState<Map<string, Billability>>(new Map())
  useEffect(() => {
    if (!billableOn) return
    if (!codesKey) {
      setBillability(new Map())
      return
    }
    let active = true
    client
      .billingIcd10Billability({ codes: codesKey.split(','), dateOfService: billableOn })
      .then((result) => {
        if (active) setBillability(new Map(result.codes.map((c) => [c.code, c])))
      })
      .catch((error) => {
        // The options stay pickable; the billing save and Submit still refuse a category code.
        logError('billing', 'Failed to check ICD-10-CM billability', error)
        if (active) setBillability(new Map())
      })
    return () => {
      active = false
    }
  }, [billableOn, client, codesKey])

  const options = useMemo(() => {
    if (billability.size === 0) return codings.map(toOption)
    const seen = new Set<string>()
    const result: DxOption[] = []
    const add = (option: DxOption) => {
      if (seen.has(option.value)) return
      seen.add(option.value)
      result.push(option)
    }
    for (const coding of codings) {
      const check = coding.code ? billability.get(coding.code) : undefined
      if (check?.billable !== false) {
        add(toOption(coding))
        continue
      }
      const header = toOption(coding)
      add({ ...header, label: `${header.label} (not billable)`, disabled: true })
      for (const child of check.children) {
        add(toOption({ system: ICD10CM_SYSTEM, code: child.code, display: child.display }))
      }
    }
    return result
  }, [billability, codings])
  const selectedKey = value?.code ? toCodingKey(value) : null
  const optionsByKey = useMemo(
    () => new Map(options.map((option) => [option.value, option.coding])),
    [options],
  )

  return (
    <Select
      size="xs"
      searchable
      clearable
      value={selectedKey}
      data={options}
      placeholder={placeholder || 'Select diagnosis'}
      onSearchChange={setSearch}
      nothingFoundMessage="No diagnoses found"
      rightSection={loading ? <span className="text-xs text-gray-500">...</span> : undefined}
      onChange={(selected) => {
        if (!selected) {
          onChange(undefined)
          return
        }

        onChange(optionsByKey.get(selected))
      }}
    />
  )
}
